import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import Retracked from 'js/app/retracked';

import CareerPlans404 from 'bundles/career-plans/components/CareerPlans404';
import CareerCards from 'bundles/career-plans/components/career-card/CareerCards';
import type {
  CareerPlansPageContentType,
  FaqItemType,
  OrderedCareerType,
} from 'bundles/career-plans/components/data/types/career-plans';
import withCareerPlansContentfulData from 'bundles/career-plans/components/data/withCareerPlansContentfulData';
import CareerPlansLayout from 'bundles/career-plans/components/layout/CareerPlansLayout';

import 'css!bundles/career-plans/components/__styles__/CareerPlansPage';

export type PropsFromCaller = {
  appSlug: string;
  faqTopic: string;
  isEntityPage?: boolean;
  hideSchemaMarkup?: boolean;
};

type ContentfulCareerPlansContentType = {
  name: string;
  description: string;
  slug: string;
  ctaCalloutText: string;
  careers: Array<OrderedCareerType>;
  faqs: Array<FaqItemType>;
  socialProofStatisticsSource?: string;
  socialProofStatisticsSourceDescription?: string;
  metaTitle?: string;
  metaDescription?: string;
};

type PropsFromStores = {
  pageContent: ContentfulCareerPlansContentType;
  loading?: boolean;
};

type Props = PropsFromCaller & PropsFromStores & CareerPlansPageContentType;

class CareerPlansPage extends React.Component<Props, {}> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof CareerPlansPage.contextTypes>;

  componentDidUpdate() {
    const { pageContent, loading } = this.props;
    const { router } = this.context;
    if (!loading && pageContent && pageContent?.careers?.length > 0) {
      const id = router.location.hash?.split('#')[1];
      const element = id && document.getElementById(id);
      if (element) {
        element.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }

  render() {
    const { pageContent, socialProofStatistics, degrees, testimonials, faq, loading } = this.props;
    if (!pageContent || !pageContent?.name) return <CareerPlans404 />;

    const {
      name,
      description,
      ctaCalloutText,
      careers,
      socialProofStatisticsSource,
      socialProofStatisticsSourceDescription,
      metaTitle,
      metaDescription,
    } = pageContent;

    return (
      <CareerPlansLayout
        pageName={name}
        pageDescription={description}
        socialProofStatistics={socialProofStatistics}
        socialProofStatisticsSource={socialProofStatisticsSource}
        socialProofStatisticsSourceDescription={socialProofStatisticsSourceDescription}
        ctaText={ctaCalloutText}
        orderedCareers={careers}
        faq={faq}
        degrees={degrees}
        testimonials={testimonials}
        metaTitle={metaTitle}
        metaDescription={metaDescription}
      >
        <main className="rc-CareerPlansPage styleguide">
          {careers && !loading && <CareerCards orderedCareers={careers} />}
        </main>
      </CareerPlansLayout>
    );
  }
}

export default compose<Props, Props>(
  withCareerPlansContentfulData(),
  Retracked.createTrackedContainer<Props>(({ pageContent }) => {
    if (!pageContent) return {};
    const { slug } = pageContent;
    if (!slug) return {};

    return {
      namespace: {
        page: slug.replace(/-+/g, '_'),
      },
    };
  })
)(CareerPlansPage);
